import React, { useState, useEffect } from 'react';
import { customersApi } from '../services/api';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';

interface Customer {
  id: string;
  shopify_customer_id?: string;
  email: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
  order_count: number;
  total_spent: number;
  last_order_at?: string;
  created_at: string;
}

export const Customers: React.FC = () => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [total, setTotal] = useState(0);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [customerDetails, setCustomerDetails] = useState<any>(null);

  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      loadCustomers();
    }, 300);
    return () => clearTimeout(delayedSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const loadCustomers = async () => {
    setLoading(true);
    try {
      const result = await customersApi.list({ search: searchQuery || undefined });
      setCustomers(result.customers || []);
      setTotal(result.total || 0);
    } catch (error) {
      console.error('Failed to load customers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewCustomer = async (customer: Customer) => {
    setSelectedCustomer(customer);
    try {
      const details = await customersApi.get(customer.id);
      setCustomerDetails(details);
    } catch (error) {
      console.error('Failed to load customer details:', error);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  return (
    <div className="p-4 lg:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header - Hidden on mobile */}
        <div className="mb-6 hidden lg:block">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Customers</h1>
          <p className="text-slate-600">View and manage your customer relationships</p>
        </div>

        {/* Search - Compact on mobile */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-subtle p-3 lg:p-4 mb-4 lg:mb-6 sticky top-0 z-20">
          <div className="flex items-center gap-3">
            <div className="flex-1 relative">
              <input
                type="text"
                placeholder="Search customers..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-sm"
              />
              <MagnifyingGlassIcon className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            </div>
            <div className="text-sm text-slate-500 whitespace-nowrap hidden sm:block">
              {total} customers
            </div>
          </div>
        </div>

        {/* Customers Table */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-subtle overflow-hidden flex flex-col" style={{ maxHeight: 'calc(100vh - 280px)' }}>
          <div className="overflow-auto flex-1">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50 sticky top-0 z-10 shadow-sm">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Orders
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Total Spent
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Last Order
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {loading && (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-slate-600">
                      Loading customers...
                    </td>
                  </tr>
                )}

                {!loading && customers.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center">
                      <div className="text-slate-400 mb-2">
                        <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                      </div>
                      <h3 className="text-lg font-medium text-slate-900 mb-2">No customers yet</h3>
                      <p className="text-slate-600">
                        Customers will appear here once orders are synced from your store
                      </p>
                    </td>
                  </tr>
                )}

                {!loading && customers.map((customer) => (
                  <tr
                    key={customer.id}
                    className="hover:bg-slate-50 transition-colors cursor-pointer"
                    onClick={() => handleViewCustomer(customer)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 flex-shrink-0">
                          <div className="h-10 w-10 rounded-full bg-brand-blue flex items-center justify-center">
                            <span className="text-white font-medium">
                              {(customer.first_name?.[0] || customer.email?.[0] || '?').toUpperCase()}
                            </span>
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-slate-900">
                            {customer.first_name} {customer.last_name}
                          </div>
                          {customer.phone && (
                            <div className="text-xs text-slate-500">{customer.phone}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-slate-600">{customer.email}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {customer.order_count} orders
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-medium text-slate-900">
                        {formatCurrency(customer.total_spent)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                      {customer.last_order_at
                        ? new Date(customer.last_order_at).toLocaleDateString()
                        : 'Never'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewCustomer(customer);
                        }}
                        className="text-sm font-medium text-brand-blue hover:text-brand-blue-hover"
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Customer Details Modal */}
        {selectedCustomer && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 z-10">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-slate-900">
                    Customer Details
                  </h2>
                  <button
                    onClick={() => {
                      setSelectedCustomer(null);
                      setCustomerDetails(null);
                    }}
                    className="text-slate-400 hover:text-slate-600"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="px-6 py-6">
                {/* Customer Info */}
                <div className="flex items-center mb-6">
                  <div className="h-16 w-16 rounded-full bg-brand-blue flex items-center justify-center">
                    <span className="text-white text-2xl font-medium">
                      {(selectedCustomer.first_name?.[0] || selectedCustomer.email?.[0] || '?').toUpperCase()}
                    </span>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold text-slate-900">
                      {selectedCustomer.first_name} {selectedCustomer.last_name}
                    </h3>
                    <p className="text-sm text-slate-600">{selectedCustomer.email}</p>
                    {selectedCustomer.phone && (
                      <p className="text-sm text-slate-500">{selectedCustomer.phone}</p>
                    )}
                  </div>
                </div>

                {/* Stats */}
                {customerDetails?.stats && (
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="bg-slate-50 rounded-lg p-4">
                      <p className="text-xs text-slate-500">Total Orders</p>
                      <p className="text-2xl font-bold text-slate-900">{customerDetails.stats.total_orders}</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-4">
                      <p className="text-xs text-slate-500">Total Spent</p>
                      <p className="text-2xl font-bold text-slate-900">
                        {formatCurrency(customerDetails.stats.total_spent)}
                      </p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-4">
                      <p className="text-xs text-slate-500">Avg Order</p>
                      <p className="text-2xl font-bold text-slate-900">
                        {formatCurrency(customerDetails.stats.avg_order_value)}
                      </p>
                    </div>
                  </div>
                )}

                {/* Recent Orders */}
                <div>
                  <h4 className="text-sm font-semibold text-slate-900 mb-3">Recent Orders</h4>
                  {customerDetails?.orders?.length > 0 ? (
                    <div className="space-y-2">
                      {customerDetails.orders.slice(0, 5).map((order: any) => (
                        <div
                          key={order.id}
                          className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                        >
                          <div>
                            <p className="text-sm font-medium text-slate-900">
                              Order #{order.order_number}
                            </p>
                            <p className="text-xs text-slate-500">
                              {new Date(order.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-medium text-slate-900">
                              {formatCurrency(order.total_price)}
                            </p>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                              order.status === 'shipped'
                                ? 'bg-green-100 text-green-700'
                                : order.status === 'awaiting_shipment'
                                ? 'bg-orange-100 text-orange-700'
                                : 'bg-slate-100 text-slate-600'
                            }`}>
                              {order.status === 'shipped' ? 'Shipped' :
                               order.status === 'awaiting_shipment' ? 'Awaiting Shipment' :
                               order.status}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500">No orders found</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
